<template>
  <div class="page-container">
    <!-- Left Sidebar Navigation -->
    <aside class="sidebar" :class="{ 'collapsed': isSidebarCollapsed }">
      <button class="sidebar-toggle" @click="toggleSidebar">
        <i :class="isSidebarCollapsed ? 'fas fa-bars' : 'fas fa-times'"></i>
      </button>
      <div class="logo-container">
        <img src="@/assets/tartoro-icon.png" alt="Tartoro Logo" class="logo" />
        <span class="logo-text" v-if="!isSidebarCollapsed">Tartoro</span>
      </div>

      <div class="nav-section">
        <h3 v-if="!isSidebarCollapsed">{{ $t('raDashboard.nav.mainMenu') }}</h3>
        <nav>
          <router-link to="/ra/dashboard" class="nav-item" active-class="active">
            <i class="fas fa-tachometer-alt"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('raDashboard.nav.dashboard') }}</span>
          </router-link>
          <router-link to="/ra/business" class="nav-item" :class="{ active: $route.path.startsWith('/ra/business') }">
            <i class="fas fa-briefcase"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('raDashboard.nav.business') }}</span>
          </router-link>
          <router-link to="/ra/incoming-mail" class="nav-item" active-class="active">
            <i class="fas fa-inbox"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('raDashboard.nav.incomingMail') }}</span>
          </router-link>
          <router-link to="/ra/mail-request" class="nav-item" active-class="active">
            <i class="fas fa-envelope"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('raDashboard.nav.mailRequest') }}</span>
          </router-link>
        </nav>
      </div>

      <div class="nav-section">
        <h3 v-if="!isSidebarCollapsed">{{ $t('raDashboard.nav.accountManagement') }}</h3>
        <nav>
          <router-link to="/ra/settings" class="nav-item" active-class="active">
            <i class="fas fa-cog"></i>
            <span v-if="!isSidebarCollapsed">{{ $t('raDashboard.nav.settings') }}</span>
          </router-link>
        </nav>
      </div>

      <div class="user-footer">
        <div class="user-info-footer" v-if="!isSidebarCollapsed">
          <img :src="user.avatar" alt="User Avatar" class="user-avatar-sm" />
          <div class="user-details">
            <span class="user-name-sm">{{ user.name }}</span>
          </div>
        </div>
        <button class="share-btn" @click="handleLogout">
          <i class="fas fa-sign-out-alt"></i>
        </button>
      </div>
    </aside>

    <!-- Main Content Area -->
    <main class="main-content" :class="{ 'expanded': isSidebarCollapsed }">
      <!-- Header -->
      <header class="header">
        <div class="header-content" style="display: flex; justify-content: flex-end; align-items: center; width: 100%;">
          <div class="language-dropdown">
            <div class="language-selector" @click="toggleDropdown">
              <span class="globe-icon">🌐</span>
              <span id="selected-language">{{ currentLanguage }}</span> ▼
            </div>
            <div class="dropdown-content" v-show="isDropdownOpen">
              <a href="#" @click.prevent="changeLanguage('en')">{{ $t('raDashboard.language.english') }}</a>
              <a href="#" @click.prevent="changeLanguage('es')">{{ $t('raDashboard.language.spanish') }}</a>
            </div>
          </div>
        </div>
      </header>

      <!-- Content Area -->
      <div class="content-area">
        <router-view :current-language="currentLanguage"></router-view>
      </div>
    </main>
  </div>
</template>

<script>
import '@/styles/CustomerHome.css'
import { useI18n } from 'vue-i18n'
import authService from '@/services/authService'

export default {
  name: 'RAHome',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      user: {
        name: '',
        role: 'Registered Agent',
        id: '',
        avatar: require('@/assets/avatar.png')
      },
      isSidebarCollapsed: false,
      isDropdownOpen: false,
      currentLanguage: 'English',
      loading: false
    }
  },
  methods: {
    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },
    changeLanguage(lang) {
      this.$i18n.locale = lang
      this.currentLanguage = lang === 'en' ? this.t('raDashboard.language.english') : this.t('raDashboard.language.spanish')
      localStorage.setItem('language', lang)
      this.isDropdownOpen = false
    },
    async loadUserData() {
      const agentEmail = localStorage.getItem('agentEmail')
      
      if (!agentEmail) {
        console.error('No agent email found in localStorage')
        this.user = {
          name: this.t('raDashboard.user.defaultName'),
          role: this.t('raDashboard.user.role'),
          id: '',
          avatar: require('@/assets/avatar.png')
        }
        return
      }

      this.loading = true
      try {
        const response = await authService.getAgentByEmail(agentEmail)

        if (response && response.success && response.agent) {
          this.user = {
            name: `${response.agent.firstName} ${response.agent.lastName}`,
            role: this.t('raDashboard.user.role'),
            id: response.agent.id,
            avatar: require('@/assets/avatar.png')
          }
        } else {
          throw new Error(response?.message || this.t('raDashboard.errors.loadFailed'))
        }
      } catch (error) {
        console.error('Error loading agent data:', error)
        this.user = {
          name: this.t('raDashboard.user.defaultName'),
          role: this.t('raDashboard.user.role'),
          id: '',
          avatar: require('@/assets/avatar.png')
        }
      } finally {
        this.loading = false
      }
    },
    async handleLogout() {
      try {
        await authService.agentLogout()
        this.$router.push('/agent/login')
      } catch (error) {
        console.error('Error during logout:', error)
      }
    }
  },
  created() {
    const savedLanguage = localStorage.getItem('language') || 'en'
    this.$i18n.locale = savedLanguage
    this.currentLanguage = savedLanguage === 'en' ? this.t('raDashboard.language.english') : this.t('raDashboard.language.spanish')
    this.loadUserData()
  },
  mounted() {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css'
    document.head.appendChild(link)
  }
}
</script>